// hooks/useTrendsLogic.ts
import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import { trendsService } from "../../../../../../services/api/trends.endpoints";
import {
  setDefaultDimension,
  setDefaultWebsiteUid,
  setIsLoadingFilter,
  setLoadingNotes,
  setOrganizationDimensions,
  setTrendsDashboardExecuteQueriesData,
} from "../../../../../../features/trendsSlice";
import store from "../../../../../../store";
import { TrendsDashboardEndPointLogic } from "../../../../../../logic/AssetManagement/Trends/TrendsDashboardEndPointLogic";
import dayjs from "dayjs";
import { TrendsDataSetQueryIds } from "../funcs/trendsAllQueryIDs";

export interface TrendsQueryParams {
  oauthTokenHeader: any;
  queryId: any;
  startDate: any;
  endDate: any;
  timeUnit: any;
  websiteSharedUids?: any[];
  dimensions?: any[];
  type: any;
  campaignId?: any;
  adgroupId?: any;
  matchtype?: any;
  kwId?: any;
  country?: any;
  trafficSource?: any;
}

export const useTrendsLogic = () => {
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    fetchBy,
    dataSet,
    selectedStartDate,
    selectedEndDate,
    organizationDimensions,
    trendsFilters,
    defaultWebsiteUid,
    defaultDimension,
  } = useSelector((state: any) => state.trends);
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const { userData } = useSelector((state: any) => state.user);

  const handleViewTrends = useCallback(
    async (selectedSharedUid: any) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("dataSet", dataSet);
      const startDateParamsRaw = newParams.get("selectedStartDate");
      const endDateParamsRaw = newParams.get("selectedEndDate");

      const startDateFormatted = startDateParamsRaw
        ? dayjs(startDateParamsRaw, "DD-MM-YYYY").format("YYYY-MM-DD")
        : selectedStartDate.format("YYYY-MM-DD");

      const endDateFormatted = endDateParamsRaw
        ? dayjs(endDateParamsRaw, "DD-MM-YYYY").format("YYYY-MM-DD")
        : selectedEndDate.format("YYYY-MM-DD");
      setSearchParams(newParams);

      const queryId =
        dataSet === "click"
          ? TrendsDataSetQueryIds.CLICK
          : TrendsDataSetQueryIds.ACTION;
      const defaultWebsiteUid = Object.keys(
        userData?.data?.website_permissions[0]
      )[0];
      const defaultDimension = userData?.data?.dimensions?.[0];
      dispatch(setDefaultWebsiteUid(defaultWebsiteUid));
      dispatch(setDefaultDimension(defaultDimension));
      const params: TrendsQueryParams = {
        oauthTokenHeader,
        queryId,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        timeUnit: "day", // This seems to need fixing in original code
        type: fetchBy === "organization" ? "dimension" : fetchBy,
      };

      params.websiteSharedUids = [defaultWebsiteUid];
      params.dimensions = [defaultDimension];

      if (fetchBy === "site") {
        params.websiteSharedUids =
          selectedSharedUid.length > 0
            ? selectedSharedUid
            : [defaultWebsiteUid];
      } else if (fetchBy === "dimension") {
        params.dimensions =
          selectedSharedUid.length > 0 ? selectedSharedUid : [defaultDimension];
      } else if (fetchBy === "organization") {
        params.dimensions =
          organizationDimensions.length > 0
            ? organizationDimensions?.map((org: any) => org.uid)
            : [defaultDimension];
      }

      if (dataSet === "click") {
        params.campaignId = "1";
        params.adgroupId = "1";
        params.matchtype = "all";
        params.kwId = "1";
        params.country = "all";
        params.trafficSource = "all";
      }
      try {
        const response = await trendsService.getTrendsDashboardExecuteQuery1(
          params
        );

        if (!response?.status || response.status !== 200) {
          throw new Error("Unexpected server response");
        }

        store.dispatch(
          setTrendsDashboardExecuteQueriesData(response.data?.data || [])
        );
        if (dataSet === "click") {
          await Promise.all([
            TrendsDashboardEndPointLogic.getGeosTrends(oauthTokenHeader),
            TrendsDashboardEndPointLogic.getTrafficSourcesTrends(
              oauthTokenHeader
            ),
          ]);
        }
      } catch (e: any) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: e?.parsedText || e?.message || "Unknown error occurred",
            type: "error",
          })
        );
      }
    },
    [
      dataSet,
      searchParams,
      setSearchParams,
      oauthTokenHeader,
      selectedStartDate,
      selectedEndDate,
      fetchBy,
      organizationDimensions,
      userData,
      dispatch,
    ]
  );

  const handleOrganizationDimensions = useCallback(async () => {
    if (fetchBy !== "organization") return;

    setIsOrgLoading(true);
    try {
      await TrendsDashboardEndPointLogic.getDimentions(oauthTokenHeader);
      const orgName = userData?.data?.user_details?.organization_name;
      const allOrgData = store.getState().trends.organizationOptions;

      if (Array.isArray(allOrgData)) {
        const matchedDimensions = allOrgData
          .filter(
            (org: any) => org.organization_id__organization_name === orgName
          )
          .map((org: any) => ({
            uid: org.dimension,
            siteName: org.dimension,
          }));

        dispatch(setOrganizationDimensions(matchedDimensions));
      }
    } finally {
      setIsOrgLoading(false);
    }
  }, [fetchBy, oauthTokenHeader, userData, dispatch]);

  const executeTrendsAPI = async (
    selectedSharedUid: any,
    overrideFilters?: any[]
  ) => {
    dispatch(setIsLoadingFilter(true));
    const queryId =
      dataSet === "click"
        ? TrendsDataSetQueryIds.CLICK
        : TrendsDataSetQueryIds.ACTION;

    const params: any = {
      oauthTokenHeader,
      queryId,
      startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
      timeUnit: "day",
      type: fetchBy === "organization" ? "dimension" : fetchBy,
    };
    params.websiteSharedUids = [defaultWebsiteUid];
    params.dimensions = [defaultDimension];

    // Website/dimension logic
    if (fetchBy === "site") {
      params.websiteSharedUids =
        selectedSharedUid.length > 0 ? selectedSharedUid : [defaultWebsiteUid];
    } else if (fetchBy === "dimension") {
      params.dimensions =
        selectedSharedUid.length > 0 ? selectedSharedUid : [defaultDimension];
    } else if (fetchBy === "organization") {
      params.dimensions =
        organizationDimensions.length > 0
          ? organizationDimensions?.map((org: any) => org.uid)
          : [defaultDimension];
    }
    const filtersToUse =
      overrideFilters !== undefined ? overrideFilters : trendsFilters;
    // 👇 Add dynamic filters from trendsFilters
    if (dataSet === "click") {
      const defaultFilters: Record<string, string> = {
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all", // always default
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };

      const filterMap: Record<string, string> = filtersToUse?.reduce(
        (acc: any, curr: { key: string; value: string }) => {
          // Normalize the key to match param names if needed
          if (curr.key.toLowerCase() === "trafficsource") {
            acc["trafficSource"] = curr.value;
          } else if (curr.key.toLowerCase() === "kwid") {
            acc["kwId"] = curr.value;
          } else {
            acc[curr.key] = curr.value;
          }
          return acc;
        },
        {}
      );

      params.campaignId = filterMap.campaignId || defaultFilters.campaignId;
      params.adgroupId = filterMap.adgroupId || defaultFilters.adgroupId;
      params.matchtype = defaultFilters.matchtype; // always default
      params.kwId = filterMap.kwId || defaultFilters.kwId;
      params.country = filterMap.country || defaultFilters.country;
      params.trafficSource =
        filterMap.trafficSource || defaultFilters.trafficSource;
    }

    // Set URL param for dataset
    const newParams = new URLSearchParams(searchParams);
    newParams.set("dataSet", dataSet);
    const filterParamKeys = [
      "trafficSource",
      "campaignId",
      "adgroupId",
      "kwId",
      "country",
      "matchtype",
    ];
    filterParamKeys.forEach((key) => newParams.delete(key));
    if (dataSet === "click") {
      // Only add non-default filter values to URL
      if (params.trafficSource && params.trafficSource !== "all") {
        newParams.set("trafficSource", params.trafficSource);
      }
      if (params.campaignId && params.campaignId !== "1") {
        newParams.set("campaignId", params.campaignId);
      }
      if (params.adgroupId && params.adgroupId !== "1") {
        newParams.set("adgroupId", params.adgroupId);
      }
      if (params.kwId && params.kwId !== "1") {
        newParams.set("kwId", params.kwId);
      }
      if (params.country && params.country !== "all") {
        newParams.set("country", params.country);
      }
    }
    setSearchParams(newParams);

    try {
      const response = await trendsService.getTrendsDashboardExecuteQuery1(
        params
      );
      if (!response?.status || response.status !== 200) {
        throw new Error("Unexpected server response");
      }

      dispatch(setTrendsDashboardExecuteQueriesData(response.data?.data || []));
    } catch (error) {
      console.error("Trends API failed:", error);
    } finally {
      dispatch(setIsLoadingFilter(false));
    }
  };
  const fetchActivityNotes = useCallback(
    async (selectedSharedUid: string[]) => {
      dispatch(setLoadingNotes(true));
      try {
        const websiteUidToUse: any =
          selectedSharedUid?.length > 0
            ? selectedSharedUid
            : Object.keys(userData?.data?.website_permissions?.[0] || {})[0];

        await TrendsDashboardEndPointLogic.getActivityNotes({
          oauthTokenHeader,
          websiteSharedUid: websiteUidToUse,
          fromDate: selectedStartDate.format("YYYY-MM-DD"),
          toDate: selectedEndDate.format("YYYY-MM-DD"),
        });
      } catch (error) {
        console.error("Error fetching activity notes:", error);
      } finally {
        dispatch(setLoadingNotes(false));
      }
    },
    [oauthTokenHeader, selectedStartDate, selectedEndDate, userData, dispatch]
  );

  // const fetchActivityNoteForUidTemp = useCallback(
  //   async (uid: string) => {
  //     dispatch(setLoadingNotes(true));
  //     try {
  //       const response = await trendsService.getSingleActivityNoteTemp(
  //         oauthTokenHeader,
  //         uid,
  //         selectedStartDate.format("YYYY-MM-DD"),
  //         selectedEndDate.format("YYYY-MM-DD")
  //       );

  //       if (response?.status === 200 && response.data?.data) {
  //         const dataObject = response.data.data;

  //         // Extract the first value (array of notes)
  //         const activityNotesArray = Object.values(dataObject)[0];

  //         if (Array.isArray(activityNotesArray)) {
  //           return activityNotesArray;
  //         }
  //       }
  //     } catch (err) {
  //       console.error(`Error fetching notes for UID: ${uid}`, err);
  //     } finally {
  //       dispatch(setLoadingNotes(false));
  //     }
  //     return []; // fallback
  //   },
  //   [oauthTokenHeader, selectedStartDate, selectedEndDate, dispatch]
  // );

  return {
    isOrgLoading,
    handleViewTrends,
    handleOrganizationDimensions,
    executeTrendsAPI,
    fetchActivityNotes,
    // fetchActivityNoteForUidTemp,
  };
};
